import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateLong } from '@utriper/core';
import { CalendarToday } from '@mui/icons-material';
import { alpha, Box, Collapse, Grid2, IconButton, InputAdornment, Popover } from '@mui/material';
import { DatePicker, TextInput, TimePickerHour, TimePickerMinutes } from '@utriper/core-ui';

const DateSelector = ({ value, onChange, meta: { touched, error }, label, ignoreError = false, ...props }) => {
    const { i18n } = useTranslation();
    const anchorRef = useRef(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [hour, setHour] = useState(value ? value?.getHours() : 12);
    const [minute, setMinute] = useState(value ? value?.getMinutes() : '00');
    const [valueParsed, setValueParsed] = useState(value);

    const handleCalendarToggle = () => {
        if (props?.disabled) return false;

        setShowCalendar(true);
    };

    const handleTimeChange = (newDate) => {
        if (!Date.parse(newDate)) return;

        setValueParsed(new Date(newDate));
    };

    useEffect(() => {
        if (!valueParsed) return;

        let date;

        if (typeof valueParsed === 'string') {
            const parsedDate = new Date(valueParsed);

            if (isNaN(parsedDate?.getTime())) {
                console.error('invalid date');
                return;
            }
            date = parsedDate;
        } else if (valueParsed instanceof Date) {
            date = valueParsed;
        } else {
            console.error('invalid date');
            return;
        }

        date.setHours(hour);
        date.setMinutes(minute);
        onChange(date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueParsed, hour, minute]);

    return (
        <Box ref={anchorRef} width="100%">
            <TextInput
                label={label}
                placeholder={props?.placeholder}
                input={{ value: formatDateLong(value, i18n.language) }}
                disabled={props?.disabled}
                fullWidth
                variant="outlined"
                slotProps={{
                    input: {
                        readOnly: true,
                        startAdornment: (
                            <InputAdornment position="start">
                                {!value && (
                                    <IconButton onClick={handleCalendarToggle} disabled={props?.disabled} size="large">
                                        <CalendarToday />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {value && (
                                    <IconButton onClick={handleCalendarToggle} disabled={props?.disabled} size="large">
                                        <CalendarToday />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        ),
                    },
                }}
                meta={{ touched: touched, error: !!(touched && error && !ignoreError) }}
                helperText={null}
            />

            <Popover
                open={showCalendar && Boolean(anchorRef.current)}
                anchorEl={anchorRef.current}
                onClose={() => setShowCalendar(false)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slots={{
                    transition: Collapse,
                }}
                slotProps={{
                    transition: {
                        timeout: 200,
                    },
                    paper: {
                        sx: {
                            background: '#fff',
                            borderRadius: '8px',
                            border: 'solid 1px #13131340',
                            padding: '8px 12px',
                            mt: '8px',
                            boxSizing: 'border-box',
                            minWidth: '150px',
                            minHeight: '150px',
                        },
                    },
                }}
            >
                <Grid2 container direction="column" gap={1} size={12}>
                    <DatePicker
                        disablePast={true}
                        sx={{
                            '& .MuiPickersDay-root': {
                                color: alpha('#131313', 0.75),
                                fontWeight: '500 !important',
                                fontSize: '12px !important',
                            },
                            '& .MuiDayCalendar-weekDayLabel': {
                                color: alpha('#131313', 0.75),
                                fontWeight: '500 !important',
                                fontSize: '12px !important',
                                background: 'transparent',
                            },
                            '& .Mui-selected': {
                                color: '#fff',
                                borderRadius: '18px',
                                backgroundColor: '#131313 !important',
                                '&:hover': {
                                    backgroundColor: '#131313',
                                },
                            },
                        }}
                        slotProps={{
                            actionBar: {
                                actions: [],
                            },
                            toolbar: {
                                sx: { display: 'none' },
                            },
                            calendarHeader: {
                                disabled: true,
                            },
                        }}
                        value={value}
                        onChange={handleTimeChange}
                        {...props}
                    />

                    <Grid2 container gap={1} flexWrap="nowrap">
                        <TimePickerHour
                            id={'datePickerHour'}
                            label="Hora"
                            value={hour || 0}
                            onFieldChange={(selected) => {
                                setHour(selected.id);
                            }}
                        />
                        <TimePickerMinutes
                            id={'datePickerMinutes'}
                            label="Minutos"
                            value={minute || 0}
                            onFieldChange={(selected) => {
                                setMinute(selected.id);
                            }}
                        />
                    </Grid2>
                </Grid2>
            </Popover>
        </Box>
    );
};

export default DateSelector;
